import { useState } from 'react';
import { PermissionGuard } from '@/features/auth';
import { Button, Card, CardContent, CardHeader, CardTitle, LoadingSpinner, Pagination, QueryErrorAlert } from '@shared/components';
import type { AuthorisedContact, AuthorisedContactsPage } from '../../../types/authorisedContacts.types';
import { canManageAuthorisedContacts } from '../../../utils/authorisedContactLabels';
import { AddAuthorisedContactDialog } from './AddAuthorisedContactDialog';
import { AuthorisedContactsTable } from './AuthorisedContactsTable';
import { ReauthoriseAuthorisedContactDialog } from './ReauthoriseAuthorisedContactDialog';
import { RevokeAuthorisedContactDialog } from './RevokeAuthorisedContactDialog';

interface Props {
    agreementId: string;
    isLoading: boolean;
    isError: boolean;
    page: AuthorisedContactsPage | undefined;
    currentPage: number;
    status: string;
    canManage: boolean;
    onRetry: () => void;
    onPageChange: (page: number) => void;
}

const headingId = 'agreement-authorised-contacts-heading';

export function AuthorisedContactsPanel({
    agreementId,
    isLoading,
    isError,
    page,
    currentPage,
    status,
    canManage,
    onRetry,
    onPageChange,
}: Props) {
    const [showForm, setShowForm] = useState(false);
    const [contactToRevoke, setContactToRevoke] = useState<AuthorisedContact | null>(null);
    const [contactToReauthorise, setContactToReauthorise] = useState<AuthorisedContact | null>(null);

    return (
        <Card>
            <CardHeader className="flex items-center justify-between gap-4">
                <CardTitle>
                    <span id={headingId}>Authorised contacts</span>
                </CardTitle>
                {canManageAuthorisedContacts(status) && (
                    <PermissionGuard permission={{ resource: 'agreement', action: 'authorised_contact.manage' }}>
                        <Button type="button" size="xs" variant="secondary" onClick={() => setShowForm(true)}>
                            Add authorised contact
                        </Button>
                    </PermissionGuard>
                )}
            </CardHeader>
            <CardContent>
                <section aria-labelledby={headingId} className="space-y-4">
                    <ContactsBody
                        isLoading={isLoading}
                        isError={isError}
                        page={page}
                        canManage={canManage}
                        onRetry={onRetry}
                        onRevoke={setContactToRevoke}
                        onReauthorise={setContactToReauthorise}
                    />

                    {page && page.meta.lastPage > 1 && (
                        <Pagination
                            currentPage={currentPage}
                            lastPage={page.meta.lastPage}
                            total={page.meta.total}
                            perPage={page.meta.perPage}
                            onPageChange={onPageChange}
                        />
                    )}
                </section>
            </CardContent>

            <AddAuthorisedContactDialog
                agreementId={agreementId}
                isOpen={showForm}
                onClose={() => setShowForm(false)}
            />

            <RevokeAuthorisedContactDialog
                agreementId={agreementId}
                contact={contactToRevoke}
                onClose={() => setContactToRevoke(null)}
            />

            <ReauthoriseAuthorisedContactDialog
                agreementId={agreementId}
                contact={contactToReauthorise}
                onClose={() => setContactToReauthorise(null)}
            />
        </Card>
    );
}

interface BodyProps {
    isLoading: boolean;
    isError: boolean;
    page: AuthorisedContactsPage | undefined;
    canManage: boolean;
    onRetry: () => void;
    onRevoke: (contact: AuthorisedContact) => void;
    onReauthorise: (contact: AuthorisedContact) => void;
}

function ContactsBody({ isLoading, isError, page, canManage, onRetry, onRevoke, onReauthorise }: BodyProps) {
    if (isLoading) {
        return <LoadingSpinner message="Loading authorised contacts" />;
    }

    if (isError || !page) {
        return (
            <QueryErrorAlert message="Failed to load the agreement's authorised contacts." onRetry={onRetry} />
        );
    }

    if (page.contacts.length === 0) {
        return (
            <p className="text-sm text-mp-muted">
                No authorised contacts have been recorded on this agreement yet.
            </p>
        );
    }

    return (
        <AuthorisedContactsTable
            contacts={page.contacts}
            emptyMessage="No authorised contacts have been recorded on this agreement yet."
            onRevoke={canManage ? onRevoke : undefined}
            onReauthorise={canManage ? onReauthorise : undefined}
        />
    );
}
